<template>
  <div class="seedream-content">
    <section class="seedream-section seedream-model-section">
      <div class="seedream-heading">
        <span>Seedream AI</span>
        <h2>{{ text('exploreTitle') }}</h2>
      </div>
      <div class="seedream-model-grid">
        <button type="button" @click="$emit('back-to-generator')">
          <div><small>Latest</small><strong>Seedream 5</strong><p>{{ text('seedream5Desc') }}</p></div><i>&rarr;</i>
        </button>
        <button type="button" @click="openSeedream4">
          <div><small>Classic</small><strong>Seedream 4</strong><p>{{ text('seedream4Desc') }}</p></div><i>&rarr;</i>
        </button>
      </div>
    </section>

    <section class="seedream-section seedream-feature-index">
      <div class="seedream-heading">
        <span>{{ modelName }}</span>
        <h2>{{ text('mainFeaturesTitle') }}</h2>
        <p>{{ text('keyFeaturesIntro') }}</p>
      </div>
      <div class="seedream-feature-links">
        <button v-for="(feature, index) in features" :key="feature.name" type="button" @click="scrollTo('seedream-feature-' + index)">
          <b>0{{ index + 1 }}</b><span><strong>{{ feature.name }}</strong><small>{{ feature.short }}</small></span>
        </button>
      </div>
    </section>

    <section class="seedream-section seedream-split-feature" id="seedream-feature-0" v-if="features[0]">
      <div class="seedream-copy"><span>01</span><h2>{{ features[0].name }}</h2><p>{{ features[0].desc }}</p></div>
      <div class="seedream-prompt-grid">
        <article v-for="(example, index) in examples(features[0]).slice(0, 2)" :key="index">
          <img :src="example.image" :alt="example.prompt" loading="lazy">
          <div><p>{{ example.prompt }}</p><button type="button" @click="$emit('use-prompt', example.prompt)">{{ $t('seo.imageModels.usePrompt') }}</button></div>
        </article>
      </div>
    </section>

    <section class="seedream-section seedream-composition" id="seedream-feature-1" v-if="features[1]">
      <figure><img :src="featureImage(features[1], 2)" :alt="features[1].name" loading="lazy"></figure>
      <div><span>02</span><h2>{{ features[1].name }}</h2><p>{{ features[1].desc }}</p><blockquote>{{ featurePrompt(features[1]) }}</blockquote><button type="button" @click="$emit('use-prompt', featurePrompt(features[1]))">{{ $t('seo.imageModels.usePrompt') }}</button></div>
    </section>

    <section class="seedream-section seedream-resolution" id="seedream-feature-2" v-if="features[2]">
      <div class="seedream-heading">
        <span>03</span><h2>{{ features[2].name }}</h2><p>{{ features[2].desc }}</p>
      </div>
      <div class="seedream-resolution-panel">
        <img :src="featureImage(features[2], 4)" :alt="features[2].name" loading="lazy">
        <div><article v-for="variant in variants" :key="variant.name"><strong>{{ variant.name }}</strong><p>{{ variant.desc }}</p><small>{{ variant.bestFor }}</small></article></div>
      </div>
    </section>

    <section class="seedream-section seedream-text-feature" id="seedream-feature-3" v-if="features[3]">
      <div class="seedream-heading">
        <span>04</span><h2>{{ features[3].name }}</h2><p>{{ features[3].desc }}</p>
      </div>
      <div class="seedream-output-grid">
        <article v-for="(example, index) in examples(features[3]).slice(0, 2)" :key="index">
          <div><span>{{ $t('seo.imageModels.demoPrompt') }}</span><p>{{ example.prompt }}</p><button type="button" @click="$emit('use-prompt', example.prompt)">{{ $t('seo.imageModels.usePrompt') }}</button></div>
          <figure><img :src="example.image" :alt="example.prompt" loading="lazy"></figure>
        </article>
      </div>
    </section>

    <section class="seedream-section seedream-detail-feature" id="seedream-feature-4" v-if="features[4]">
      <div><span>05</span><h2>{{ features[4].name }}</h2><p>{{ features[4].desc }}</p><button type="button" @click="$emit('use-prompt', featurePrompt(features[4]))">{{ $t('seo.imageModels.usePrompt') }}</button></div>
      <figure><img :src="featureImage(features[4], 8)" :alt="features[4].name" loading="lazy"></figure>
    </section>

    <section class="seedream-section seedream-how-to">
      <div class="seedream-heading"><span>{{ text('howToKicker') }}</span><h2>{{ text('howToTitle') }}</h2><p>{{ text('howToIntro') }}</p></div>
      <div class="seedream-step-grid">
        <article v-for="(step, index) in howToSteps" :key="step.title"><strong>0{{ index + 1 }}</strong><h3>{{ step.title }}</h3><p>{{ step.desc }}</p></article>
      </div>
      <button class="seedream-primary-button" type="button" @click="$emit('back-to-generator')">{{ text('howToCta') }}</button>
    </section>

    <section class="seedream-section seedream-gallery">
      <div class="seedream-heading"><span>{{ text('showcaseKicker') }}</span><h2>{{ text('showcaseTitle') }}</h2><p>{{ text('showcaseIntro') }}</p></div>
      <div class="seedream-gallery-grid">
        <article v-for="(example, index) in galleryExamples" :key="index"><img :src="example.image" :alt="example.prompt" loading="lazy"><p>{{ example.prompt }}</p></article>
      </div>
    </section>

    <section class="seedream-section seedream-use-cases">
      <div class="seedream-heading"><span>{{ text('useCasesKicker') }}</span><h2>{{ text('useCasesTitle') }}</h2></div>
      <div class="seedream-use-grid"><article v-for="(item, index) in useCases" :key="item.name"><b>0{{ index + 1 }}</b><div><h3>{{ item.name }}</h3><p>{{ item.desc }}</p></div></article></div>
    </section>

    <section class="seedream-section seedream-faq">
      <div class="seedream-heading"><span>FAQ</span><h2>{{ text('faqTitle') }}</h2></div>
      <div><details v-for="faq in faqs" :key="faq.q"><summary><span>{{ faq.q }}</span><i></i></summary><p>{{ faq.a }}</p></details></div>
    </section>

    <section class="seedream-cta">
      <div><span>Seedream AI · Miral</span><h2>{{ text('ctaTitle') }}</h2><p>{{ text('ctaDesc') }}</p><button type="button" @click="$emit('back-to-generator')">{{ text('howToCta') }}</button></div>
      <div class="seedream-cta-images"><img :src="fallbackImages[6]" :alt="text('ctaTitle')" loading="lazy"><img :src="fallbackImages[7]" :alt="text('ctaTitle')" loading="lazy"></div>
    </section>
  </div>
</template>

<script>
var FALLBACK_IMAGES = [
  require('@/assets/images/seedream/0217834949526105a9f949eb72c2227dac5c405ca3d9cd6c996b9_0.webp'),
  require('@/assets/images/seedream/021783495149924e4fd4c285bd49f804239cddc30345c48dc6024_0.webp'),
  require('@/assets/images/seedream/994ae318-58d7-4ffa-98e1-ea97553ff0c0.webp'),
  require('@/assets/images/seedream/021783495389730ffa263910bfaab0198f27d0234753c494fd2fa_0.webp'),
  require('@/assets/images/seedream/0217834956211361c17ef9c173e8babb4a0658490f0169e937059_0.webp'),
  require('@/assets/images/seedream/0217834957232579f663fff68d336b1dca042ed755c42d2babd5a_0.webp'),
  require('@/assets/images/seedream/021783495813482572fdc12977fa128088f145c73a50ff1524591_0.webp'),
  require('@/assets/images/seedream/021783495894855ad8f78454eef31552c89ebf2f2559e7d2d47a0_0.webp')
]

module.exports = {
  name: 'SeedreamSeoContent',
  props: {
    modelName: { type: String, default: 'Seedream 5' },
    features: { type: Array, default: function () { return [] } },
    variants: { type: Array, default: function () { return [] } },
    useCases: { type: Array, default: function () { return [] } },
    faqs: { type: Array, default: function () { return [] } },
    howToSteps: { type: Array, default: function () { return [] } }
  },
  data: function () { return { fallbackImages: FALLBACK_IMAGES } },
  computed: {
    galleryExamples: function () {
      var result = []
      var self = this
      this.features.forEach(function (feature, index) {
        var featureExamples = self.examples(feature)
        featureExamples.forEach(function (example) { result.push(example) })
        if (!featureExamples.length) result.push({ prompt: self.featurePrompt(feature), image: self.featureImage(feature, index) })
      })
      return result.slice(0, 8)
    }
  },
  methods: {
    // 读取 Seedream 当前语言的专属页面文案。
    text: function (field) { return this.$t('seo.imageModels.seedream.' + field) },
    // 将单图与多行案例统一成双列提示词和输出图片数据。
    examples: function (feature) {
      if (!feature) return []
      var rows = feature.demo && feature.demo.rows ? feature.demo.rows : []
      if (rows.length) return rows
      if (feature.image) return [{ prompt: feature.prompt || feature.short, image: feature.image }]
      return []
    },
    // 获取功能的第一条提示词，供首屏输入框复用。
    featurePrompt: function (feature) {
      var rows = this.examples(feature)
      return rows.length ? rows[0].prompt : (feature.prompt || feature.short || feature.name)
    },
    // 获取功能图片，缺失时按顺序使用 Seedream 现有素材。
    featureImage: function (feature, index) {
      var rows = this.examples(feature)
      return rows.length ? rows[0].image : (feature.image || this.fallbackImages[index % this.fallbackImages.length])
    },
    // 平滑定位到对应的 Seedream 功能说明区。
    scrollTo: function (id) {
      var target = document.getElementById(id)
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    // 打开当前语言下的 Seedream 4 生成页面。
    openSeedream4: function () {
      var lang = this.$route.params.lang
      this.$router.push((lang ? '/' + lang : '') + '/models/seedream-4')
    }
  }
}
</script>

<style scoped>
.seedream-content { width: 100%; max-width: 1060px; margin: 0 auto; color: #191b20; }
.seedream-section { margin-bottom: 108px; scroll-margin-top: 88px; }
.seedream-heading { max-width: 820px; margin: 0 auto 36px; text-align: center; }
.seedream-heading > span, .seedream-copy > span, .seedream-composition > div > span, .seedream-detail-feature > div > span { color: #6b5bd2; font-size: 11px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
.seedream-heading h2, .seedream-copy h2, .seedream-composition h2, .seedream-detail-feature h2 { margin: 10px 0 12px; font-size: clamp(29px, 3.4vw, 41px); line-height: 1.18; letter-spacing: -.035em; }
.seedream-heading p, .seedream-copy p, .seedream-composition > div > p, .seedream-detail-feature > div > p { margin: 0; color: #666c76; font-size: 14px; line-height: 1.75; }
.seedream-model-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.seedream-model-grid button { display: flex; min-height: 174px; padding: 28px; align-items: center; justify-content: space-between; border: 1px solid #e1e3e8; border-radius: 18px; background: linear-gradient(145deg, #fff, #f6f4ff); color: #202229; cursor: pointer; text-align: left; transition: transform .2s, border-color .2s, box-shadow .2s; }
.seedream-model-grid button:hover { transform: translateY(-3px); border-color: #bcb3f2; box-shadow: 0 16px 36px rgba(67, 55, 143, .09); }
.seedream-model-grid small { color: #7566d7; font-size: 10px; font-weight: 800; text-transform: uppercase; }
.seedream-model-grid strong { display: block; margin: 12px 0 7px; font-size: 22px; }
.seedream-model-grid p { max-width: 360px; margin: 0; color: #737984; font-size: 13px; line-height: 1.6; }
.seedream-model-grid i { color: #7566d7; font-size: 24px; font-style: normal; }
.seedream-feature-index { padding: 66px 58px; border-radius: 28px; background: #f6f7fb; }
.seedream-feature-links { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.seedream-feature-links button { display: flex; min-height: 106px; padding: 20px; align-items: flex-start; gap: 17px; border: 1px solid #e1e3e8; border-radius: 14px; background: #fff; cursor: pointer; text-align: left; }
.seedream-feature-links b { color: #8a7ce1; font-size: 12px; }
.seedream-feature-links span { display: flex; flex-direction: column; gap: 7px; }
.seedream-feature-links strong { color: #252830; font-size: 15px; }
.seedream-feature-links small { color: #777d87; font-size: 12px; line-height: 1.55; }
.seedream-split-feature { display: grid; grid-template-columns: .72fr 1.28fr; gap: 54px; align-items: start; }
.seedream-copy { position: sticky; top: 100px; }
.seedream-prompt-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
.seedream-prompt-grid article { overflow: hidden; border: 1px solid #e1e3e7; border-radius: 16px; background: #fff; }
.seedream-prompt-grid img { width: 100%; aspect-ratio: 1 / 1; object-fit: cover; }
.seedream-prompt-grid article > div { padding: 18px; }
.seedream-prompt-grid p, .seedream-gallery-grid p { margin: 0 0 13px; color: #4f555f; font-size: 12px; line-height: 1.65; }
.seedream-prompt-grid button, .seedream-output-grid button, .seedream-composition button, .seedream-detail-feature button { padding: 0; border: 0; background: transparent; color: #6b5bd2; cursor: pointer; font-size: 12px; font-weight: 750; }
.seedream-composition, .seedream-detail-feature { display: grid; grid-template-columns: 1.08fr .92fr; gap: 56px; align-items: center; }
.seedream-composition figure, .seedream-detail-feature figure { margin: 0; overflow: hidden; border-radius: 22px; background: #f0f1f4; }
.seedream-composition img, .seedream-detail-feature img { display: block; width: 100%; max-height: 600px; object-fit: cover; }
.seedream-composition blockquote { margin: 24px 0 14px; padding: 18px; border-left: 3px solid #8a7ce1; background: #f7f6fd; color: #4e545e; font-size: 13px; line-height: 1.65; }
.seedream-resolution-panel { display: grid; grid-template-columns: 1.1fr .9fr; overflow: hidden; border: 1px solid #e0e2e7; border-radius: 22px; background: #f7f7fa; }
.seedream-resolution-panel > img { width: 100%; height: 100%; min-height: 520px; object-fit: cover; }
.seedream-resolution-panel > div { display: flex; padding: 34px; flex-direction: column; justify-content: center; gap: 14px; }
.seedream-resolution-panel article { padding: 20px; border: 1px solid #e2e3e8; border-radius: 13px; background: #fff; }
.seedream-resolution-panel strong { color: #6b5bd2; font-size: 20px; }
.seedream-resolution-panel p { margin: 8px 0; color: #5e646e; font-size: 12px; line-height: 1.55; }
.seedream-resolution-panel small { color: #9196a0; font-size: 10px; }
.seedream-output-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.seedream-output-grid article { display: grid; grid-template-columns: .86fr 1.14fr; overflow: hidden; border: 1px solid #e0e2e7; border-radius: 16px; background: #fff; }
.seedream-output-grid article > div { display: flex; padding: 24px; flex-direction: column; align-items: flex-start; justify-content: center; }
.seedream-output-grid span { color: #989da6; font-size: 10px; font-weight: 800; text-transform: uppercase; }
.seedream-output-grid p { margin: 15px 0; color: #4e545e; font-size: 12px; line-height: 1.65; }
.seedream-output-grid figure { margin: 0; overflow: hidden; background: #eef0f2; }
.seedream-output-grid img { width: 100%; height: 100%; min-height: 300px; object-fit: cover; }
.seedream-detail-feature { grid-template-columns: .82fr 1.18fr; padding: 62px; border-radius: 26px; background: linear-gradient(145deg, #f4f2ff, #eefaf6); }
.seedream-how-to { padding: 70px 58px; border-radius: 28px; background: #181a20; color: #fff; }
.seedream-how-to .seedream-heading h2 { color: #fff; }
.seedream-how-to .seedream-heading p { color: #b7bbc4; }
.seedream-step-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
.seedream-step-grid article { min-height: 210px; padding: 25px; border: 1px solid rgba(255,255,255,.09); border-radius: 15px; background: rgba(255,255,255,.04); }
.seedream-step-grid strong { color: #a99cf2; font-size: 32px; }
.seedream-step-grid h3 { margin: 32px 0 10px; color: #fff; font-size: 16px; }
.seedream-step-grid p { margin: 0; color: #b6bac3; font-size: 12px; line-height: 1.65; }
.seedream-primary-button { display: block; margin: 30px auto 0; padding: 13px 24px; border: 0; border-radius: 10px; background: #fff; color: #1b1d22; cursor: pointer; font-weight: 750; }
.seedream-gallery-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.seedream-gallery-grid article { overflow: hidden; border: 1px solid #e1e3e7; border-radius: 17px; background: #fff; }
.seedream-gallery-grid img { display: block; width: 100%; aspect-ratio: 16 / 10; object-fit: cover; }
.seedream-gallery-grid p { margin: 0; padding: 17px 19px; }
.seedream-use-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
.seedream-use-grid article { display: grid; grid-template-columns: 42px 1fr; gap: 14px; padding: 25px; border: 1px solid #e0e2e6; border-radius: 15px; }
.seedream-use-grid b { color: #8274dc; font-size: 13px; }
.seedream-use-grid h3 { margin: 0 0 8px; font-size: 16px; }
.seedream-use-grid p { margin: 0; color: #737984; font-size: 13px; line-height: 1.65; }
.seedream-faq { max-width: 860px; margin-right: auto; margin-left: auto; }
.seedream-faq > div:last-child { border-top: 1px solid #dfe1e5; }
.seedream-faq details { border-bottom: 1px solid #dfe1e5; }
.seedream-faq summary { display: flex; padding: 22px 2px; align-items: center; justify-content: space-between; gap: 20px; cursor: pointer; font-size: 15px; font-weight: 700; list-style: none; }
.seedream-faq summary::-webkit-details-marker { display: none; }
.seedream-faq summary i { position: relative; width: 14px; height: 14px; flex: 0 0 14px; }
.seedream-faq summary i::before, .seedream-faq summary i::after { content: ''; position: absolute; top: 7px; left: 1px; width: 12px; height: 1px; background: #747a84; }
.seedream-faq summary i::after { transform: rotate(90deg); }
.seedream-faq details[open] summary i::after { transform: rotate(0); }
.seedream-faq details p { margin: -2px 0 22px; color: #676d77; font-size: 14px; line-height: 1.75; }
.seedream-cta { display: grid; min-height: 390px; grid-template-columns: 1fr 1fr; overflow: hidden; border-radius: 26px; background: radial-gradient(circle at 10% 10%, rgba(183,170,255,.65), transparent 35%), linear-gradient(135deg, #f2efff, #e7f8f1); }
.seedream-cta > div:first-child { align-self: center; padding: 56px; }
.seedream-cta > div:first-child > span { color: #6b5bd2; font-size: 11px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.seedream-cta h2 { margin: 14px 0; font-size: clamp(29px, 3.4vw, 42px); line-height: 1.15; }
.seedream-cta p { margin: 0; color: #5f6570; font-size: 14px; line-height: 1.7; }
.seedream-cta button { margin-top: 25px; padding: 13px 22px; border: 0; border-radius: 10px; background: #191b20; color: #fff; cursor: pointer; font-weight: 750; }
.seedream-cta-images { display: grid; grid-template-columns: repeat(2, 1fr); gap: 3px; }
.seedream-cta-images img { width: 100%; height: 100%; min-width: 0; object-fit: cover; }
@media (max-width: 900px) {
  .seedream-split-feature, .seedream-composition, .seedream-detail-feature, .seedream-resolution-panel, .seedream-cta { grid-template-columns: 1fr; }
  .seedream-copy { position: static; }
  .seedream-resolution-panel > img { min-height: 360px; }
  .seedream-step-grid { grid-template-columns: 1fr; }
  .seedream-step-grid article { min-height: 0; }
  .seedream-cta-images { height: 320px; }
}
@media (max-width: 640px) {
  .seedream-section { margin-bottom: 76px; }
  .seedream-model-grid, .seedream-feature-links, .seedream-prompt-grid, .seedream-output-grid, .seedream-gallery-grid, .seedream-use-grid { grid-template-columns: 1fr; }
  .seedream-feature-index, .seedream-how-to, .seedream-detail-feature { margin-right: -18px; margin-left: -18px; padding: 48px 18px; border-radius: 0; }
  .seedream-output-grid article { grid-template-columns: 1fr; }
  .seedream-output-grid figure { min-height: 280px; }
  .seedream-resolution-panel > div { padding: 22px; }
  .seedream-cta { margin-right: -18px; margin-left: -18px; border-radius: 0; }
  .seedream-cta > div:first-child { padding: 42px 22px; }
  .seedream-cta-images { height: 260px; }
}
</style>
